import { createApiRef } from '@backstage/frontend-plugin-api';

export interface InventoryRepo {
  id: number;
  repo: string;
  defaultBranch: string | null;
  archived: boolean;
  nvmrc: string | null;
  nodeVersionFile: string | null;
  pkgEnginesNode: string | null;
  scanError: string | null;
  scannedAt: string | null;
  blacklisted: boolean;
  blacklistReason: string | null;
  codeowners: string[];
  githubActions: Array<{ workflowFile: string; signal: string }>;
  circleci: string[];
  dockerfile: string[];
}

export interface InventoryStats {
  repos: number;
  archived: number;
  errors: number;
  withGha: number;
  withCircleci: number;
  withDockerfile: number;
  lastScannedAt: string | null;
}

export interface BlacklistEntry {
  repo: string;
  reason: string;
  createdAt: string;
}

// Mirrors nodejs/Release/schedule.json: { "v18": { start, lts?, maintenance?, end, codename? }, ... }
export interface NodeReleaseEntry {
  start: string;
  lts?: string;
  maintenance?: string;
  end: string;
  codename?: string;
}

export interface NodeScheduleResponse {
  schedule: Record<string, NodeReleaseEntry>;
  fetchedAt: string;
  source: string;
  fetchError?: string;
}

// One row from versions-manager's Node.js feed (`?vendor=nodejs`). The
// `accountName` field is whatever the external producer emits per row —
// usually zone-shaped (e.g. `imt-production`, `slave-eu1-production`) but
// sometimes service-shaped (e.g. `web-api`, `certman`).
export interface DeployedNodeVersion {
  accountName: string;
  version: string;
  lastUpdated: string;
}

export type DeployedNodeStatus =
  | 'ok'
  | 'disabled'
  | 'empty'
  | 'error'
  | 'pending';

export interface DeployedNodeVersionsResult {
  status: DeployedNodeStatus;
  entries: DeployedNodeVersion[];
  fetchedAt: string | null;
  error?: string;
  httpStatus?: number;
}

// Minimal projection of deployments-backend's AppDTO — only the fields
// node-inventory consumes to render the per-cluster section and column.
export interface ArgoApp {
  name: string;
  repo?: string;
  clusters: Array<{
    cluster: string;
    dockerTag?: string;
    status?: string;
    lastSyncAt?: string;
    appVersion?: string;
  }>;
}

export type ArgoAppsStatus = 'ok' | 'disabled' | 'empty' | 'error' | 'pending';

export interface ArgoAppsResult {
  status: ArgoAppsStatus;
  apps: ArgoApp[];
  fetchedAt: string | null;
  error?: string;
  httpStatus?: number;
}

export interface NodeInventoryApi {
  getInventory(): Promise<{ stats: InventoryStats; repos: InventoryRepo[] }>;
  getNodeSchedule(): Promise<NodeScheduleResponse>;
  getBlacklist(): Promise<{ blacklist: BlacklistEntry[] }>;
  addToBlacklist(repo: string, reason: string): Promise<void>;
  removeFromBlacklist(repo: string): Promise<void>;
  triggerScan(): Promise<void>;
  triggerArgoRefresh(): Promise<void>;
  triggerDeployedNodeRefresh(): Promise<void>;
  getDeployedNodeVersions(): Promise<DeployedNodeVersionsResult>;
  getArgoApps(): Promise<ArgoAppsResult>;
}

export const nodeInventoryApiRef = createApiRef<NodeInventoryApi>().with({
  id: 'plugin.node-inventory.api',
});
